import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { motion, AnimatePresence } from "framer-motion";
import mermaid from "mermaid";
import { 
  Play, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  FileText,
  Download,
  RefreshCw,
  ChevronRight,
  Brain,
  Zap,
  Database,
  Users,
  GitBranch,
  Shield,
  Layout,
  ClipboardList,
  HelpCircle,
  Upload,
  GitCompare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Initialize Mermaid
mermaid.initialize({ 
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'loose',
  fontSize: 14
});

const sectionIcons = {
  executive_summary: Brain,
  scope_assumptions: ClipboardList,
  role_definitions: Users,
  target_state: GitBranch,
  functional_requirements: FileText,
  non_functional_requirements: Shield,
  business_rules: Zap,
  data_model: Database,
  integration_map: Database,
  user_experience: Layout,
  flow_diagram: GitCompare,
  test_scenarios: ClipboardList,
  risk_analysis: AlertCircle,
  open_items: HelpCircle,
};

const agentInfo = {
  intake_context: { name: "Intake & Context", icon: Brain, color: "text-blue-400" },
  domain_interpretation: { name: "Domain Interpretation", icon: Zap, color: "text-purple-400" },
  requirements_engineer: { name: "Requirements Engineer", icon: FileText, color: "text-green-400" },
  process_modeling: { name: "Process Modeling", icon: GitBranch, color: "text-orange-400" },
  data_integration: { name: "Data & Integration", icon: Database, color: "text-cyan-400" },
  ux_journey: { name: "UX & Journey", icon: Layout, color: "text-pink-400" },
  qa_risk: { name: "QA & Risk", icon: Shield, color: "text-red-400" },
  critic: { name: "CRITIC", icon: AlertCircle, color: "text-yellow-400" },
};

export default function AnalysisStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [sections, setSections] = useState({});
  const [activeSection, setActiveSection] = useState("executive_summary");
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [agentConfig, setAgentConfig] = useState({});
  const [retrying, setRetrying] = useState(false);
  const mermaidContainerRef = useRef(null);

  // Render mermaid diagrams when content changes
  useEffect(() => {
    if (activeSection === "flow_diagram" && analysis?.sections[activeSection]?.content) {
      renderMermaidDiagrams();
    }
  }, [activeSection, analysis]);

  const renderMermaidDiagrams = async () => {
    if (!mermaidContainerRef.current) return;
    
    try {
      const container = mermaidContainerRef.current;
      const content = analysis.sections[activeSection]?.content || "";
      
      // Find all mermaid code blocks
      const mermaidRegex = /```mermaid\n([\s\S]*?)```/g;
      const matches = [...content.matchAll(mermaidRegex)];
      
      if (matches.length === 0) return;

      // Clear container
      container.innerHTML = "";

      // Render each diagram
      for (let i = 0; i < matches.length; i++) {
        const mermaidCode = matches[i][1].trim();
        const diagramDiv = document.createElement("div");
        diagramDiv.className = "mermaid-diagram mb-8 p-4 bg-secondary/20 rounded-lg overflow-x-auto";
        diagramDiv.id = `mermaid-${i}`;
        container.appendChild(diagramDiv);

        try {
          const { svg } = await mermaid.render(`diagram-${i}`, mermaidCode);
          diagramDiv.innerHTML = svg;
        } catch (error) {
          console.error("Mermaid render error:", error);
          diagramDiv.innerHTML = `<div class="text-destructive text-sm">Diyagram render hatası: ${error.message}</div>`;
        }
      }
    } catch (error) {
      console.error("Mermaid rendering failed:", error);
    }
  };

  const fetchData = async (showError = true) => {
    try {
      const axiosConfig = { timeout: 10000 };
      const [projectRes, analysesRes, configRes, sectionsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, axiosConfig),
        axios.get(`${API}/projects/${projectId}/analyses`, axiosConfig),
        axios.get(`${API}/agents/config`, axiosConfig),
        axios.get(`${API}/sections`, axiosConfig)
      ]);

      const projectData = projectRes.data;
      setProject(projectData);
      setAgentConfig(configRes.data);
      setSections(sectionsRes.data);

      // Check if analyzing based on project status
      const isAnalyzing = projectData.status === "analyzing";
      setAnalyzing(isAnalyzing);

      if (analysesRes.data.length > 0) {
        const latestAnalysis = analysesRes.data[0];
        setAnalysis(latestAnalysis);
      } else {
        setAnalysis(null);
      }
    } catch (error) {
      if (showError && loading) {
        toast.error("Veri yüklenirken hata oluştu");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  // Poll every 2 seconds while analyzing
  useEffect(() => {
    let interval;
    if (analyzing) {
      interval = setInterval(() => fetchData(false), 2000);
    }
    return () => clearInterval(interval);
  }, [analyzing]);

  const startAnalysis = async () => {
    if (analyzing) {
      toast.error("Analiz zaten devam ediyor");
      return;
    }
    
    try {
      await axios.post(`${API}/projects/${projectId}/analyze`);
      toast.success("Analiz başlatıldı!");
      setAnalyzing(true);
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Analiz başlatılamadı");
    }
  };

  const retryFailedSections = async () => {
    if (retrying || analyzing) {
      toast.error("İşlem devam ediyor");
      return;
    }
    
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/projects/${projectId}/analyses/retry`);
      toast.success(response.data.message);
      setAnalyzing(true);
      fetchData(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Yeniden deneme başlatılamadı");
    } finally {
      setRetrying(false);
    }
  };

  const getProgress = () => {
    if (!analysis?.sections) return 0;
    const completed = Object.values(analysis.sections).filter(s => s.status === "completed" || s.status === "error").length;
    return Math.round((completed / 14) * 100);
  };

  const getAgentStatus = (agentId) => {
    if (!project?.agent_progress) return "pending";
    return project.agent_progress[agentId] || "pending";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-8 text-center">
        <p className="text-muted-foreground">Proje bulunamadı</p>
      </div>
    );
  }

  const isAnalyzing = analyzing || project.status === "analyzing";
  const isCompleted = project.status === "completed";

  return (
    <div className="h-screen flex flex-col overflow-hidden" data-testid="analysis-studio">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-xl px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold truncate">{project.name}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              {project.description}
              {isAnalyzing && <span className="ml-2 text-blue-400">(Analiz devam ediyor...)</span>}
              {isCompleted && <span className="ml-2 text-emerald-400">(Tamamlandı)</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {(analysis?.status === "completed" || isCompleted) && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate(`/project/${projectId}/export`)}
                data-testid="export-btn"
                className="text-xs sm:text-sm"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            <Button
              onClick={startAnalysis}
              disabled={isAnalyzing}
              size="sm"
              className="btn-glow text-xs sm:text-sm"
              data-testid="start-analysis-btn"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin sm:mr-2" />
                  <span className="hidden sm:inline">Analiz... {getProgress()}%</span>
                  <span className="sm:hidden">{getProgress()}%</span>
                </>
              ) : analysis || isCompleted ? (
                <>
                  <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Yeniden Analiz</span>
                  <span className="sm:hidden">Yeniden</span>
                </>
              ) : (
                <>
                  <Play className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Analizi Başlat</span>
                  <span className="sm:hidden">Başlat</span>
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Progress Bar */}
        {(isAnalyzing || analysis) && (
          <div className="mt-3 sm:mt-4">
            <div className="flex items-center justify-between text-xs sm:text-sm mb-2">
              <span className="text-muted-foreground">Analiz İlerlemesi</span>
              <span className="font-medium">{getProgress()}%</span>
            </div>
            <Progress value={getProgress()} className="h-1.5 sm:h-2" />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left Panel - Agents (Hidden on mobile, shown as dropdown or collapsible) */}
        <div className="hidden lg:block lg:w-80 border-r border-border bg-card/30 overflow-y-auto p-4">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Brain className="w-4 h-4 text-primary" />
            AI Agents
          </h2>
          <div className="space-y-2">
            {Object.entries(agentInfo).map(([agentId, info]) => {
              const status = getAgentStatus(agentId);
              const Icon = info.icon;
              
              return (
                <motion.div
                  key={agentId}
                  className={`p-3 rounded-lg border transition-all ${
                    status === "completed" 
                      ? "border-emerald-500/30 bg-emerald-500/5" 
                      : status === "running"
                      ? "border-blue-500/30 bg-blue-500/5"
                      : "border-border bg-background/50"
                  }`}
                  data-testid={`agent-${agentId}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Object.keys(agentInfo).indexOf(agentId) * 0.05 }}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg bg-secondary flex items-center justify-center ${info.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{info.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {agentConfig[agentId]?.models?.join(" + ") || ""}
                      </p>
                    </div>
                    <div className={`status-dot ${status}`} />
                  </div>
                  {status === "running" && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-blue-400">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      İşleniyor...
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Documents Section */}
          <div className="mt-6">
            <h3 className="font-medium mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Dokümanlar
            </h3>
            {project.documents?.length > 0 ? (
              <div className="space-y-2">
                {project.documents.map((doc, i) => (
                  <div key={i} className="p-2 bg-secondary/50 rounded text-sm truncate">
                    {doc.name}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Doküman yüklenmedi</p>
            )}
          </div>
        </div>

        {/* Right Panel - Analysis Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {analysis?.sections && Object.keys(analysis.sections).length > 0 ? (
            <>
              {/* Section Tabs - Horizontal Scroll - Mobile Optimized */}
              <div className="border-b border-border bg-card/30">
                <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-secondary">
                  <div className="flex p-2 gap-1.5 sm:gap-2 min-w-max">
                    {sections.sections?.map((sectionId) => {
                      const section = analysis.sections[sectionId];
                      const Icon = sectionIcons[sectionId] || FileText;
                      const isCompleted = section?.status === "completed";
                      const hasError = section?.status === "error";
                      
                      return (
                        <button
                          key={sectionId}
                          onClick={() => setActiveSection(sectionId)}
                          className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-all flex-shrink-0 ${
                            activeSection === sectionId
                              ? "bg-primary text-primary-foreground shadow-md"
                              : isCompleted
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20"
                              : hasError
                              ? "bg-destructive/10 text-destructive border border-destructive/30"
                              : "bg-secondary/30 text-muted-foreground hover:bg-secondary/50 border border-transparent"
                          }`}
                          data-testid={`section-tab-${sectionId}`}
                        >
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          <span>{sections.titles?.[sectionId] || sectionId}</span>
                          {isCompleted && activeSection !== sectionId && (
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Section Content */}
              <ScrollArea className="flex-1 p-3 sm:p-4 md:p-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                    className="max-w-4xl mx-auto"
                  >
                    {analysis.sections[activeSection] ? (
                      <div className="prose-content" data-testid="section-content">
                        <h1>{sections.titles?.[activeSection] || activeSection}</h1>
                        
                        {/* Show error state with retry button */}
                        {analysis.sections[activeSection]?.status === "error" && (
                          <div className="mb-6 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                            <div className="flex items-start gap-3">
                              <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-medium text-destructive mb-1">Bu bölüm analiz edilirken hata oluştu</p>
                                <p className="text-sm text-muted-foreground mb-3">
                                  {analysis.sections[activeSection]?.content || "API hatası nedeniyle analiz tamamlanamadı."}
                                </p>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={retryFailedSections}
                                  disabled={retrying || analyzing}
                                  className="border-destructive/50 hover:bg-destructive/10"
                                >
                                  {retrying ? (
                                    <>
                                      <Loader2 className="w-3 h-3 animate-spin mr-2" />
                                      Yeniden Deneniyor...
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="w-3 h-3 mr-2" />
                                      Başarısız Bölümleri Yeniden Dene
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Render Flow Diagram with Mermaid */}
                        {activeSection === "flow_diagram" && analysis.sections[activeSection]?.status === "completed" ? (
                          <div>
                            <div className="mb-6 text-muted-foreground">
                              <ReactMarkdown>
                                {analysis.sections[activeSection]?.content?.split("```mermaid")[0] || ""}
                              </ReactMarkdown>
                            </div>
                            <div ref={mermaidContainerRef} className="mermaid-container"></div>
                          </div>
                        ) : activeSection !== "flow_diagram" && analysis.sections[activeSection]?.status === "completed" ? (
                          <ReactMarkdown>
                            {analysis.sections[activeSection]?.content || "İçerik henüz oluşturulmadı."}
                          </ReactMarkdown>
                        ) : null}
                        
                        {analysis.sections[activeSection]?.source_refs?.length > 0 && (
                          <div className="mt-8 p-4 bg-secondary/30 rounded-lg">
                            <p className="text-sm font-medium mb-2">Kaynaklar:</p>
                            <div className="flex flex-wrap gap-2">
                              {analysis.sections[activeSection].source_refs.map((ref, i) => (
                                <Badge key={i} variant="outline">{ref}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
                        <p>Bu bölüm henüz analiz edilmedi.</p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </ScrollArea>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="w-20 h-20 rounded-2xl bg-primary/10 mx-auto mb-6 flex items-center justify-center">
                  <Brain className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-2xl font-semibold mb-2">Analiz Başlatın</h2>
                <p className="text-muted-foreground mb-6">
                  {project.idea_text || project.documents?.length > 0
                    ? "8 AI agent ile profesyonel iş analizi paketinizi oluşturmaya başlayın."
                    : "Analiz başlatmadan önce proje fikri yazın veya doküman yükleyin."}
                </p>
                
                {!project.idea_text && (!project.documents || project.documents.length === 0) ? (
                  <Button variant="outline" onClick={() => navigate(`/new-project`)}>
                    <Upload className="w-4 h-4 mr-2" />
                    İçerik Ekle
                  </Button>
                ) : (
                  <Button onClick={startAnalysis} className="btn-glow" disabled={analyzing}>
                    {analyzing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Başlatılıyor...
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Analizi Başlat
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
